import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import StatsBar from "./components/StatsBar";
import ProductsTab from "./components/ProductsTab";
import OrdersTab from "./components/OrdersTab";
import DropshippingTab from "./components/DropshippingTab";

type Tab = "overview" | "products" | "orders" | "dropshipping";

const tabs: { id: Tab; label: string; icon: string }[] = [
  { id: "overview", label: "Overview", icon: "ri-dashboard-line" },
  { id: "products", label: "My Products", icon: "ri-price-tag-3-line" },
  { id: "orders", label: "Order Requests", icon: "ri-shopping-bag-3-line" },
  { id: "dropshipping", label: "Dropshipping", icon: "ri-truck-line" },
];

const SupplierDashboardPage = () => {
  const [activeTab, setActiveTab] = useState<Tab>("overview");

  return (
    <div className="min-h-screen bg-amber-50/20">
      <Navbar />
      <div className="pt-24 pb-20 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">

          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 flex-shrink-0 rounded-2xl overflow-hidden border-2 border-amber-300">
                <img
                  src="https://public.readdy.ai/ai/img_res/4a61579923e3113a376f9e25138a3115.jpg"
                  alt="Factory Owner"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="font-black text-gray-900 text-xl" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Al Futtaim Industrial Group
                  </h1>
                  <span className="bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                    <i className="ri-shield-check-fill text-xs"></i> Verified
                  </span>
                </div>
                <p className="text-gray-400 text-sm">Dubai, UAE · Member since 2024 · <span className="text-emerald-600 font-semibold">Dropshipping Active</span></p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/register-factory"
                className="flex items-center gap-2 border border-amber-200 text-amber-700 hover:bg-amber-50 font-semibold text-sm px-4 py-2 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line"></i> Add Product
              </Link>
              <button className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold text-sm px-4 py-2 rounded-xl transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-settings-3-line"></i> Settings
              </button>
            </div>
          </div>

          {/* Tab Nav */}
          <div className="flex gap-1 bg-white border border-amber-100 rounded-2xl p-1.5 mb-8 w-fit flex-wrap">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab.id
                    ? "bg-amber-500 text-white"
                    : "text-gray-500 hover:text-amber-600 hover:bg-amber-50"
                }`}
              >
                <i className={tab.icon}></i>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Stats always visible */}
          <StatsBar />

          {/* Tab Content */}
          {activeTab === "overview" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Orders */}
              <div className="bg-white border border-amber-100 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-black text-gray-900 text-base flex items-center gap-2">
                    <i className="ri-shopping-bag-3-line text-amber-500"></i> Recent Orders
                  </h3>
                  <button onClick={() => setActiveTab("orders")} className="text-amber-600 text-xs font-semibold hover:underline cursor-pointer whitespace-nowrap">View All</button>
                </div>
                <div className="space-y-3">
                  {[
                    { name: "James Mitchell", country: "UK", product: "LED Smart Panel 200W", total: "$9,000", type: "bulk", status: "new" },
                    { name: "Yuki Tanaka", country: "Japan", product: "Oud Perfume 100ml", total: "$12,500", type: "bulk", status: "confirmed" },
                    { name: "Carlos Mendez", country: "USA", product: "Security Camera 4K", total: "$475", type: "sample", status: "shipped" },
                  ].map((o, i) => (
                    <div key={i} className="flex items-center gap-3 py-2 border-b border-amber-50 last:border-0">
                      <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                        <i className="ri-user-line text-amber-600 text-sm"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 text-xs">{o.name} <span className="text-gray-400 font-normal">· {o.country}</span></p>
                        <p className="text-gray-400 text-xs truncate">{o.product}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-bold text-gray-900 text-xs">{o.total}</p>
                        <span className={`text-xs font-semibold ${o.status === "new" ? "text-amber-600" : o.status === "confirmed" ? "text-emerald-600" : "text-sky-600"}`}>
                          {o.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top Products */}
              <div className="bg-white border border-amber-100 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-black text-gray-900 text-base flex items-center gap-2">
                    <i className="ri-bar-chart-line text-amber-500"></i> Top Products
                  </h3>
                  <button onClick={() => setActiveTab("products")} className="text-amber-600 text-xs font-semibold hover:underline cursor-pointer whitespace-nowrap">Manage</button>
                </div>
                <div className="space-y-3">
                  {[
                    { name: "Luxury Arabic Oud Perfume 100ml", orders: 87, views: 3210, pct: 92 },
                    { name: "Smart Home Security Camera 4K", orders: 61, views: 2654, pct: 70 },
                    { name: "Premium Medjool Dates Gift Box", orders: 45, views: 1540, pct: 52 },
                    { name: "Industrial LED Smart Panel 200W", orders: 34, views: 1842, pct: 39 },
                  ].map((p, i) => (
                    <div key={i} className="py-2 border-b border-amber-50 last:border-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-semibold text-gray-900 text-xs truncate max-w-[200px]">{p.name}</p>
                        <span className="text-xs text-gray-500 flex-shrink-0 ml-2">{p.orders} orders</span>
                      </div>
                      <div className="h-1.5 bg-amber-50 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-400 rounded-full" style={{ width: `${p.pct}%` }}></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Dropship Summary */}
              <div className="bg-white border border-amber-100 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-black text-gray-900 text-base flex items-center gap-2">
                    <i className="ri-truck-line text-emerald-500"></i> Dropship Partners
                  </h3>
                  <button onClick={() => setActiveTab("dropshipping")} className="text-amber-600 text-xs font-semibold hover:underline cursor-pointer whitespace-nowrap">View All</button>
                </div>
                <div className="space-y-3">
                  {[
                    { name: "GulfTradersHub", platform: "Facebook", sales: 210, commission: "$4,200", status: "active" },
                    { name: "TechReview_UK", platform: "YouTube", sales: 142, commission: "$2,840", status: "active" },
                    { name: "@luxe_drops", platform: "TikTok", sales: 89, commission: "$1,780", status: "active" },
                  ].map((p, i) => (
                    <div key={i} className="flex items-center gap-3 py-2 border-b border-amber-50 last:border-0">
                      <div className="w-8 h-8 flex items-center justify-center bg-emerald-100 rounded-full flex-shrink-0">
                        <i className="ri-user-star-line text-emerald-600 text-sm"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 text-xs">{p.name}</p>
                        <p className="text-gray-400 text-xs">{p.platform} · {p.sales} sales</p>
                      </div>
                      <span className="font-bold text-emerald-600 text-xs flex-shrink-0">{p.commission}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-6 text-white">
                <h3 className="font-black text-lg mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Quick Actions</h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { icon: "ri-add-circle-line", label: "Add Product", action: () => setActiveTab("products") },
                    { icon: "ri-message-3-line", label: "View RFQs", action: () => {} },
                    { icon: "ri-truck-line", label: "Dropship Settings", action: () => setActiveTab("dropshipping") },
                    { icon: "ri-bar-chart-line", label: "View Analytics", action: () => {} },
                  ].map((a) => (
                    <button
                      key={a.label}
                      onClick={a.action}
                      className="flex items-center gap-2 bg-white/20 hover:bg-white/30 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className={`${a.icon} text-base`}></i>
                      {a.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === "products" && <ProductsTab />}
          {activeTab === "orders" && <OrdersTab />}
          {activeTab === "dropshipping" && <DropshippingTab />}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SupplierDashboardPage;
